import endpoints from '@app/src/api/endpoints'
import { useFileDownload } from '@app/src/api/fetchHooks'
import AiSkippedQuestionInfo from '@app/src/components/Drawer/Views/AiSkippedQuestionInfo'
import { QuestionType } from '@app/src/types/reporting'
import type { AssessmentRule, ResponseItem } from '@app/src/types/resourceExplorer'
import { Check, Close, DownloadOutlined } from '@mui/icons-material'
import { Button, Chip, Stack, Tooltip } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { getFileScore, getOptionScores } from './assessmentAnswersLogic'

interface BaselineAnswerProps {
  item: ResponseItem
  rules: AssessmentRule[]
  showPoints: boolean
}

interface ScoreChipProps {
  label: string
  isSelected: boolean
  isRewarded: boolean
  points: number
  showPoints: boolean
}

const ScoreChip: React.FC<ScoreChipProps> = ({ label, isSelected, isRewarded, points, showPoints }) => {
  const { formatMessage } = useIntl()
  const isHighlighted = isSelected && isRewarded
  const tooltip = showPoints ? formatMessage({ id: 'baselineAssessment.providerPage.points' }, { points }) : ''

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        disabled={!isSelected}
        icon={isSelected ? <Check /> : <Close />}
        label={label}
        sx={({ palette }) => ({
          maxWidth: 240,
          ...(isSelected && {
            bgcolor: isRewarded ? palette.brandDecorative.emerald : palette.error.main,
            color: isHighlighted ? palette.common.white : palette.text.primary,
            '& .MuiChip-icon': { color: isHighlighted ? palette.common.white : palette.text.primary },
          }),
        })}
      />
    </Tooltip>
  )
}

const FileChips: React.FC<BaselineAnswerProps> = ({ item, rules, showPoints }) => {
  const { formatMessage } = useIntl()
  const { downloadFile, isFileDownloading } = useFileDownload()
  const { hasFile, isRewarded, points } = getFileScore(item, rules)
  const fileId = item.uploadReferences?.[0]?.id

  return (
    <>
      <ScoreChip
        label={hasFile ? String(item.answer) : formatMessage({ id: 'baselineAssessment.providerPage.noFileUploaded' })}
        isSelected={hasFile || isRewarded}
        isRewarded={isRewarded}
        points={points}
        showPoints={showPoints}
      />
      {fileId !== undefined && (
        <Button
          id="downloadFileButton"
          size="small"
          sx={{ minWidth: 'auto' }}
          disabled={isFileDownloading}
          onClick={() => downloadFile(endpoints.fileDownload(fileId))}
        >
          <DownloadOutlined fontSize="small" />
        </Button>
      )}
    </>
  )
}

const OptionChips: React.FC<BaselineAnswerProps> = ({ item, rules, showPoints }) => (
  <>
    {getOptionScores(item, rules).map(option => (
      <ScoreChip
        key={option.value}
        label={option.value}
        isSelected={option.isSelected}
        isRewarded={option.isRewarded}
        points={option.points}
        showPoints={showPoints}
      />
    ))}
  </>
)

const BaselineAnswer: React.FC<BaselineAnswerProps> = props => {
  const { item, rules } = props
  const questionType = item.requestItem.template?.questionTypeId ?? item.requestItem.questionTypeId
  const isFileQuestion = questionType === QuestionType.File
  const hasAnswer = isFileQuestion ? getFileScore(item, rules).hasFile : (item.selectedOptions?.length ?? 0) > 0

  return (
    <Stack>
      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
        {isFileQuestion ? <FileChips {...props} /> : <OptionChips {...props} />}
      </Stack>
      {item.isScraped && !hasAnswer && <AiSkippedQuestionInfo />}
    </Stack>
  )
}

export default BaselineAnswer
